// Redeem codes, client side (spec 2026-10-09). The RPCs re-check everything:
// admin_* refuse anyone but an admin, redeem_code records the redemption and
// queues the rewards as ONE dev_inbox row of kind 'code', which the inbox
// drain applies through applyCodeRewards.
import type { SupabaseClient } from '@supabase/supabase-js';
import { applyGive } from './applyGive';
import type { StorageLike } from './saveSync';
import { parseRewards, rewardLabel, type CodeReward } from '../config/redeemCodes';
import { availableTowers } from '../config/unlocks';
import { AVATAR_ICONS_KEY } from '../config/avatars';
import { LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS } from '../config/levels';
import { ProgressService } from '../services/progressService';

let client: SupabaseClient | null = null;

/** Wired once in main.ts; null (no Supabase env) leaves the codes screen asking to sign in. */
export function initCodes(accountClient: SupabaseClient | null): void {
  client = accountClient;
}

export function codesClient(): SupabaseClient | null {
  return client;
}

export interface AdminCode {
  code: string;
  rewards: CodeReward[];
}

type Result<T> = { ok: true; value: T } | { ok: false; error: string };

export async function adminListCodes(c: SupabaseClient): Promise<Result<AdminCode[]>> {
  const { data, error } = await c.rpc('admin_list_codes');
  if (error) return { ok: false, error: error.message };
  const rows = (data ?? []) as { code: string; rewards: unknown }[];
  return {
    ok: true,
    value: rows.map((r) => ({ code: r.code, rewards: parseRewards(r.rewards) ?? [] })),
  };
}

export async function adminSaveCode(
  c: SupabaseClient,
  code: string,
  rewards: CodeReward[],
  isNew: boolean,
): Promise<Result<null>> {
  const { error } = await c.rpc('admin_save_code', {
    p_code: code,
    p_rewards: rewards,
    p_is_new: isNew,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, value: null };
}

export async function adminDeleteCode(c: SupabaseClient, code: string): Promise<Result<null>> {
  const { error } = await c.rpc('admin_delete_code', { p_code: code });
  if (error) return { ok: false, error: error.message };
  return { ok: true, value: null };
}

export type RedeemOutcome = 'ok' | 'unknown' | 'used';

export async function redeemCode(c: SupabaseClient, code: string): Promise<Result<RedeemOutcome>> {
  const { data, error } = await c.rpc('redeem_code', { p_code: code });
  if (error) return { ok: false, error: error.message };
  const status = (data as { status?: unknown } | null)?.status;
  if (status === 'ok' || status === 'unknown' || status === 'used')
    return { ok: true, value: status };
  return { ok: false, error: 'Unexpected reply' };
}

export async function codesAvailable(c: SupabaseClient): Promise<Result<number>> {
  const { data, error } = await c.rpc('codes_available');
  if (error) return { ok: false, error: error.message };
  return { ok: true, value: typeof data === 'number' ? data : 0 };
}

/**
 * Put a code's rewards into the save and return the labels for what actually
 * landed. A tower or icon the player already had is left out of the list
 * (the brief: "it won't mention giving it to you"); null = malformed payload.
 */
export function applyCodeRewards(storage: StorageLike, raw: unknown): string[] | null {
  const rewards = parseRewards(raw);
  if (!rewards) return null;
  const progress = new ProgressService(storage, LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
  const towers = new Set<string>(
    availableTowers(progress.enteredLevels(), progress.unlockedExtras()),
  );
  const icons = readList(storage, AVATAR_ICONS_KEY);
  const landed: string[] = [];
  for (const r of rewards) {
    if (r.what === 'tower' && towers.has(r.value)) continue;
    if (r.what === 'icon' && icons.includes(r.value)) continue;
    let ok: boolean;
    if (r.what === 'item' || r.what === 'potion') {
      // applyGive puts ONE item or potion on the shelf per call.
      ok = true;
      for (let i = 0; i < r.amount; i++)
        ok = applyGive(storage, { what: r.what, value: r.value }) && ok;
    } else if (r.what === 'tower' || r.what === 'icon') {
      ok = applyGive(storage, { what: r.what, value: r.value });
    } else {
      ok = applyGive(storage, { what: r.what, value: r.amount });
    }
    if (ok) landed.push(rewardLabel(r));
  }
  return landed;
}

function readList(storage: StorageLike, key: string): string[] {
  try {
    const v: unknown = JSON.parse(storage.getItem(key) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
